import type { CreateCommunicationInputPayload, UpdateCommunicationInputPayload } from '../../../../../hooks/Marketing';
import type { DefinitionFormData } from '../CampaignDefinitionStep';
import type { TargetingFormData } from '../CampaignTargetingStep';
import { CHANNEL_ID } from '../channels';

type CreationPayload = Record<string, unknown> | null;

type WithoutNulls<T> = { [K in keyof T]: Exclude<T[K], null> };

const omitNulls = <T extends object>(values: T): WithoutNulls<T> =>
  Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value ?? undefined])) as WithoutNulls<T>;

const editionDates = (definition: DefinitionFormData) =>
  definition.isPreEdited
    ? { start: '', end: '' }
    : { start: definition.editionDateStart, end: definition.editionDateEnd };

const buildFields = (definition: DefinitionFormData, targeting: TargetingFormData) => ({
  defChannelId: CHANNEL_ID[definition.channel],
  defTitle: definition.title.trim(),
  defDescription: definition.description.trim() || null,
  defSendStart: (definition.channel === 'cashdesk' ? editionDates(definition).start : definition.sendDate) || null,
  defNotSendable: definition.sendByCustomer ? 1 : 0,
  targetTitle: targeting.title.trim() || undefined,
  targetDescription: targeting.description.trim() || null,
  targetQueryId: targeting.queryId,
  targetCategoryId: targeting.cashdeskTarget?.targetCategoryId ?? null,
});

const buildMeta = (definition: DefinitionFormData, targeting: TargetingFormData) => {
  const edition = editionDates(definition);
  return {
    _queryDesc: targeting.queryDescription.trim() || null,
    _listId: targeting.listId,
    _cashdeskIdentifierTypeId: targeting.cashdeskTarget?.identifierTypeId ?? null,
    _cashdeskEntityIds: targeting.cashdeskTarget?.entityIds ?? null,
    _hasOffer: definition.hasOffer,
    _followUpDateStart: definition.hasOffer ? null : definition.followUpDateStart || null,
    _followUpDateEnd: definition.hasOffer ? null : definition.followUpDateEnd || null,
    _isTestOperation: definition.isTestOperation,
    _isRecurring: definition.isRecurring,
    _isPreEdited: definition.isPreEdited,
    _noCreation: definition.channel === 'cashdesk' && definition.noCreation,
    _editionDateStart: edition.start || null,
    _editionDateEnd: edition.end || null,
  };
};

export const creationContentKeys = (payload: CreationPayload): string[] =>
  Object.keys(payload ?? {}).filter(key => !key.startsWith('_'));

export const buildCreatePayload = (
  definition: DefinitionFormData,
  targeting: TargetingFormData,
  creation: CreationPayload,
): CreateCommunicationInputPayload => ({
  ...omitNulls(buildFields(definition, targeting)),
  creationPayload: { ...creation, ...omitNulls(buildMeta(definition, targeting)) },
});

export const buildUpdatePayload = (
  definition: DefinitionFormData,
  targeting: TargetingFormData,
  creation: CreationPayload,
  staleCreationKeys: ReadonlySet<string>,
): UpdateCommunicationInputPayload => {
  const current = creation ?? {};
  const cleared = Object.fromEntries([...staleCreationKeys].filter(key => !(key in current)).map(key => [key, null]));
  return {
    ...buildFields(definition, targeting),
    creationPayload: { ...cleared, ...current, ...buildMeta(definition, targeting) },
  };
};
